import { AppError, err, ok } from '@aibi/shared';
import { parseUserIntent } from '../intent/parse-user-intent.js';
import type { AIProvider } from '../provider.js';
import type { AIModelOutputInput } from '../protocol.js';

export interface LocalRulesProviderOptions {
  readonly id?: string;
  readonly displayName?: string;
}

/**
 * Proveedor local y determinista basado en `parseUserIntent`: no usa red ni modelos de
 * lenguaje. Emite el mismo protocolo JSON que un proveedor remoto, por lo que su salida
 * recorre exactamente el mismo pipeline de validacion.
 *
 * @example
 * const core = createAICore({ provider: createLocalRulesProvider() });
 */
export function createLocalRulesProvider(options: LocalRulesProviderOptions = {}): AIProvider {
  const id = options.id ?? 'local-rules';
  return {
    id,
    kind: 'local',
    displayName: options.displayName ?? 'Interprete local (reglas)',
    capabilities: { structuredOutput: true, streaming: false },
    generate(request, signal) {
      if (signal?.aborted === true) {
        return Promise.resolve(err(new AppError('CANCELLED', 'Solicitud de IA cancelada.')));
      }
      const intent = parseUserIntent(request.userMessage, request.context);
      let output: AIModelOutputInput;
      switch (intent.kind) {
        case 'actions':
          output = { kind: 'actions', actions: [...intent.actions], rationale: intent.rationale };
          break;
        case 'clarification':
          output = {
            kind: 'clarification',
            question: intent.clarification.question,
            reason: intent.clarification.reason,
            candidates: intent.clarification.candidates.map((candidate) => ({ ...candidate })),
            affectedActionIds: [...intent.clarification.affectedActionIds],
            ...(intent.clarification.term === undefined ? {} : { term: intent.clarification.term }),
          };
          break;
        case 'error':
          output = {
            kind: 'error',
            code: intent.error.code,
            message: intent.error.message,
            ...(intent.error.candidates === undefined
              ? {}
              : { candidates: intent.error.candidates.map((candidate) => ({ ...candidate })) }),
          };
          break;
      }
      return Promise.resolve(
        ok({ providerId: id, model: 'rules-v1', content: JSON.stringify(output) }),
      );
    },
  };
}
